import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldAlert } from "lucide-react";

export default function ForceChangePassword() {
  const { reload, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) return setError("New password must be at least 8 characters.");
    if (next !== confirm) return setError("Passwords do not match.");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      await reload();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="w-full max-w-sm p-7 fadeup">
        <Logo size={28} />
        <div className="flex items-center gap-2 mt-6 text-primary">
          <ShieldAlert className="w-5 h-5" />
          <h1 className="font-display text-xl font-bold tracking-tight">Set a new password</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          For your security, you must replace your temporary password before continuing.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Temporary password</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required data-testid="fcp-current-input" className="h-11" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">New password</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} required data-testid="fcp-new-input" className="h-11" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Confirm new password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="fcp-confirm-input" className="h-11" />
          </div>
          {error && <p className="text-sm text-destructive" data-testid="fcp-error">{error}</p>}
          <Button type="submit" disabled={saving} className="w-full h-11" data-testid="fcp-submit-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
          </Button>
        </form>
        <button onClick={logout} className="text-xs text-muted-foreground mt-4 hover:text-foreground" data-testid="fcp-logout">
          Sign out instead
        </button>
      </Card>
    </div>
  );
}
